import { useNavigate, useSearch } from "@tanstack/react-router";
import { AlertTriangle, ArrowRight, CheckCircle, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { authClient } from "@/lib/auth-client";

interface InvitePeek {
  status: "pending" | "accepted" | "revoked" | "expired";
  solutionName: string | null;
  solutionId: string | null;
  clientName: string | null;
  email: string;
  role: string;
}

type LoadState =
  | { phase: "loading" }
  | { phase: "loaded"; peek: InvitePeek }
  | { phase: "invalid" }
  | { phase: "error"; message: string };

export function AcceptPage() {
  const search = useSearch({ from: "/accept" });
  const navigate = useNavigate();
  const token = search.token ?? "";

  const [state, setState] = useState<LoadState>({ phase: "loading" });
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [autoAcceptTried, setAutoAcceptTried] = useState(false);

  // Resolve auth state + invite peek in parallel on mount.
  useEffect(() => {
    let alive = true;
    (async () => {
      const sessionRes = await authClient.getSession();
      if (!alive) return;
      setAuthed(Boolean(sessionRes.data));

      if (!token) {
        setState({ phase: "invalid" });
        return;
      }
      try {
        const res = await fetch(`/api/invites/${encodeURIComponent(token)}`);
        if (!alive) return;
        if (res.status === 404) {
          setState({ phase: "invalid" });
          return;
        }
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          setState({
            phase: "error",
            message: body?.error?.message ?? "Failed to load invite",
          });
          return;
        }
        const json = await res.json();
        setState({ phase: "loaded", peek: json.data });
      } catch {
        if (alive) setState({ phase: "error", message: "Failed to load invite" });
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  // Auto-fire the accept call when an authed user lands on a pending invite,
  // so they don't have to click a second "Accept" button after signup/login.
  useEffect(() => {
    if (autoAcceptTried) return;
    if (state.phase !== "loaded") return;
    if (!authed) return;
    if (state.peek.status !== "pending") return;
    setAutoAcceptTried(true);
    accept();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, authed, autoAcceptTried]);

  async function accept() {
    if (state.phase !== "loaded") return;
    setAccepting(true);
    try {
      const res = await fetch(`/api/invites/${encodeURIComponent(token)}/accept`, {
        method: "POST",
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        toast.error(json?.error?.message ?? "Failed to accept invite");
        // Refresh peek — the server has the authoritative status.
        const peekRes = await fetch(`/api/invites/${encodeURIComponent(token)}`);
        if (peekRes.ok) {
          const peekJson = await peekRes.json();
          setState({ phase: "loaded", peek: peekJson.data });
        }
        setAccepting(false);
        return;
      }
      const { solutionId, alreadyMember } = json.data;
      toast.success(alreadyMember ? "Already a member — taking you there" : "Joined!");
      navigate({ to: "/s/$solutionId/intake", params: { solutionId } });
    } catch {
      toast.error("Failed to accept invite");
      setAccepting(false);
    }
  }

  return (
    <AuthLayout>
      <Card className="border-border/50 shadow-sm">
        <CardHeader className="space-y-1 text-center pb-2">
          <h1 className="font-display text-3xl font-bold">Solution invite</h1>
        </CardHeader>
        <CardContent>{renderBody()}</CardContent>
      </Card>
    </AuthLayout>
  );

  function renderBody() {
    if (state.phase === "loading" || authed === null) {
      return (
        <div className="flex items-center justify-center py-8 text-muted-foreground gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Loading invite…</span>
        </div>
      );
    }

    if (state.phase === "invalid") {
      return (
        <Notice
          icon={<AlertTriangle className="h-5 w-5" />}
          title="Invalid invite link"
          body="This link doesn't match a known invite. Ask whoever invited you to share a fresh one."
        />
      );
    }

    if (state.phase === "error") {
      return (
        <Notice
          icon={<AlertTriangle className="h-5 w-5" />}
          title="Couldn't load invite"
          body={state.message}
        />
      );
    }

    const { peek } = state;

    if (peek.status === "revoked") {
      return (
        <Notice
          icon={<AlertTriangle className="h-5 w-5" />}
          title="Invite revoked"
          body="The person who invited you cancelled this link. Ask them to send a new one."
        />
      );
    }
    if (peek.status === "expired") {
      return (
        <Notice
          icon={<AlertTriangle className="h-5 w-5" />}
          title="Invite expired"
          body="This invite is past its 7-day window. Ask the inviter to send a new one."
        />
      );
    }
    if (peek.status === "accepted") {
      return (
        <Notice
          icon={<CheckCircle className="h-5 w-5 text-emerald-600" />}
          title="Already accepted"
          body="This invite has already been used. Sign in to see the solution."
          action={
            <Button onClick={() => navigate({ to: "/login" })}>
              Sign in <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
            </Button>
          }
        />
      );
    }

    // pending — show solution info + accept (or auth-then-accept) action
    const inviteLine = (
      <>
        Invited to{" "}
        <span className="font-medium text-foreground">{peek.solutionName ?? "a solution"}</span>
        {peek.clientName && (
          <>
            {" "}
            · <span className="text-muted-foreground">{peek.clientName}</span>
          </>
        )}
      </>
    );

    if (!authed) {
      return (
        <div className="space-y-4 text-center py-2">
          <p className="text-sm text-muted-foreground">{inviteLine}</p>
          <p className="text-xs text-muted-foreground">
            Sign in or create an account as <span className="font-mono">{peek.email}</span> to
            accept.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              onClick={() =>
                navigate({
                  to: "/login",
                  search: { invite: token },
                })
              }
            >
              Sign in
            </Button>
            <Button
              onClick={() =>
                navigate({
                  to: "/signup",
                  search: { invite: token, email: peek.email },
                })
              }
            >
              Create account
            </Button>
          </div>
        </div>
      );
    }

    // Authed + pending: auto-accept is firing (or just fired). Show a spinner
    // and a manual fallback button if it somehow didn't kick off.
    return (
      <div className="space-y-4 text-center py-2">
        <p className="text-sm text-muted-foreground">{inviteLine}</p>
        {accepting || autoAcceptTried ? (
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Joining…</span>
          </div>
        ) : (
          <Button onClick={accept} disabled={accepting} className="w-full">
            Accept invite
          </Button>
        )}
      </div>
    );
  }
}

function Notice({
  icon,
  title,
  body,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="space-y-3 text-center py-4">
      <div className="flex justify-center text-amber-700 dark:text-amber-300">{icon}</div>
      <div>
        <h2 className="font-display text-lg font-semibold">{title}</h2>
        <p className="text-sm text-muted-foreground mt-1">{body}</p>
      </div>
      {action && <div className="flex justify-center">{action}</div>}
    </div>
  );
}
